import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import { HttpStatus } from '@catalyze/ui';
import { LEADS_PAGE_SIZE } from 'api/constants/leads';
import { useGetFiltersQuery } from 'api/leads/getFilters';
import { getLeads } from 'api/leads/getLeads';
import { useGetLeadsWorthQuery } from 'api/leads/getLeadsWorth';
import type { ILeadsSortParams } from 'api/types/lead';
import { Status } from 'api/types/lead';
import { formatFiltersForAPI } from 'components/Filter/utils/filters';
import { Table } from 'components/Table';
import { useInfiniteTableData } from 'components/Table/helpers/useInfiniteTableData';
import { Columns } from 'constants/columns';
import type { Filters } from 'constants/filters';
import { LeadGroups } from 'constants/leadGroups';
import type { SortOrder } from 'constants/sort';
import { NonPaidEmptyState } from 'features/RealEstate/components/NonPaidEmptyState';
import { RealEstateListPageContainer } from 'features/RealEstate/components/RealEstateListPageContainer';
import type { TFilterState } from 'features/RealEstate/context/FilterContext';
import { filtersInitialState } from 'features/RealEstate/context/FilterContext';
import { usePropertyUrl } from 'features/RealEstate/utils/usePropertyUrl';

import { PageTitle } from '../../components/PageTitle';

export const Archived: FC = () => {
    const didMount = useRef(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState('');
    const [isAllSelected, setAllSelected] = useState<boolean>(false);
    const [filtersFormData, setFilters] =
        useState<Record<Filters, TFilterState>>(filtersInitialState);
    const filtersDebounce = useDebounce(filtersFormData, 500);

    const sortBy = searchParams.get('sortBy');
    const sortOrder = searchParams.get('sortOrder') as SortOrder;

    const navigateToProperty = usePropertyUrl({
        sortBy,
        sortOrder,
        status: Status.ARCHIVED,
    });
    const { data: filters } = useGetFiltersQuery({
        status: Status.ARCHIVED,
    });

    const { data: filteredNetWorth, refetch: filteredNetWorthRefetch } =
        useGetLeadsWorthQuery({
            status: Status.ARCHIVED,
            search: '',
            activeFilters: { ...filtersFormData },
            enabled: false,
        });

    const { data: totalNetWorth, refetch: totalNetWorthRefetch } =
        useGetLeadsWorthQuery({
            status: Status.ARCHIVED,
            search: '',
            activeFilters: filtersInitialState,
        });

    useEffect(() => {
        if (didMount.current) {
            didMount.current = false;
        } else if (filteredNetWorthRefetch) {
            filteredNetWorthRefetch();
        }
    }, [filtersDebounce]);

    useEffect(() => {
        totalNetWorthRefetch();
    }, []);

    const { error, ...tableProps } = useInfiniteTableData({
        status: Status.ARCHIVED,
        search,
        sortParams: {
            sortBy: sortBy ?? undefined,
            sortOrder: sortOrder ?? undefined,
        },
        filters: filtersFormData,
    });

    const fetchExportData = async () => {
        const exportData = await getLeads({
            status: Status.ARCHIVED,
            search: '',
            filterQueryParams: formatFiltersForAPI(filtersFormData),
            pageSize: filteredNetWorth?.count,
        });
        return exportData;
    };

    const isUserDisabled = error?.response?.status === HttpStatus.FORBIDDEN;

    return (
        <RealEstateListPageContainer>
            {isUserDisabled ? (
                <NonPaidEmptyState />
            ) : (
                <>
                    <PageTitle type={LeadGroups.ARCHIVED} />
                    <Table
                        filters={filters}
                        leadGroup={LeadGroups.ARCHIVED}
                        tableColumns={[
                            Columns.ADDRESS,
                            Columns.CITY,
                            Columns.STATE,
                            Columns.ZIP,
                            Columns.EDM,
                            Columns.LEAD_TYPE,
                            Columns.DATE_ADDED,
                            Columns.MARKET_VALUE,
                        ]}
                        onPropertyClick={navigateToProperty}
                        onSearchHandler={(searchString: string) => {
                            setSearch(searchString.trim());
                        }}
                        onFiltersChange={(filters) => setFilters(filters)}
                        isAllSelected={isAllSelected}
                        onSelectAll={(isSelected) => {
                            if (
                                filteredNetWorth?.count &&
                                filteredNetWorth.count > LEADS_PAGE_SIZE
                            )
                                setAllSelected(isSelected);
                        }}
                        onSortApply={(sortParams?: ILeadsSortParams) => {
                            if (!sortParams) {
                                searchParams.delete('sortBy');
                                searchParams.delete('sortOrder');
                                setSearchParams(searchParams, {
                                    replace: true,
                                });
                            } else {
                                setSearchParams(
                                    sortParams as { [key: string]: string },
                                    { replace: true }
                                );
                            }
                        }}
                        displayedMarketValue={
                            filteredNetWorth?.estimatedMarketValue
                        }
                        totalMarketValue={totalNetWorth?.estimatedMarketValue}
                        nrOfLoadedResults={filteredNetWorth?.count}
                        nrOfResults={filteredNetWorth?.allLeads}
                        fetchExportData={fetchExportData}
                        {...tableProps}
                    />
                </>
            )}
        </RealEstateListPageContainer>
    );
};
